<template>
   <Head>
      <title>{{ $t("meta.signup.title") }}</title>
      <meta name="description" :content="$t('meta.signup.content')" />
   </Head>

   <PageLoader :isLoading="isLoading" :loadErrors="loadErrors">
      <transition name="portal-fade" mode="out-in">
         <!-- Error with reg code or other param, or email does not match reg code -->
         <!--state.regCodeError-->
         <AuthLayout
            v-if="state.regCodeError"
            :heading="$t('authentication.create_account')"
            class="flex-col items-center"
            data-cy="reg-code-error"
         >
            <template v-slot:description>
               <div data-cy="reg-with-code-error-message" class="reg-with-code-error-message">
                  <p v-if="state.regCodeError === 'Invalid StudySite identifier'" data-cy="site-id-error">
                     {{ $t("authentication.site_id_problem") }}
                  </p>
                  <p v-else-if="state.regCodeError === 'Invalid registration code'" data-cy="user-reg-code-error">
                     {{ $t("authentication.wrong_email") }}
                  </p>
                  <p v-else data-cy="portal-invite-code-error">
                     {{ $t("authentication.reg_invite_problem") }}
                  </p>

                  <DefaultButton class="mt-8 w-full" data-cy="try-again" @click="onReset">
                     {{ $t("authentication.try_again") }}
                  </DefaultButton>
               </div>
            </template>

            <template v-slot:bottom>
               <a
                  v-if="organisationInfo.email"
                  :href="`mailto:${organisationInfo.email}`"
                  class="text-regular text-brand-dark underline"
               >
                  {{ $t("authentication.get_in_touch") }}
               </a>
            </template>
         </AuthLayout>

         <!-- Signup completed (open your email) -->
         <!--state.isRegCompleted-->
         <AuthLayout v-else-if="state.isRegCompleted" :heading="$t('authentication.create_account')">
            <template v-slot:form>
               <p data-cy="signup-email-verification-message" class="my-6 leading-tight">
                  {{ $t("authentication.email_verification_message") }}
               </p>
               <p data-cy="signup-email-sent" class="text-bolder mb-6 text-center">{{ state.userEmail }}</p>
               <p class="text-bold text-base leading-tight">{{ $t("authentication.check_inbox_with_instructions") }}</p>

               <!-- registration completed -->
               <EmailClientLinks class="mt-8" />
            </template>
         </AuthLayout>

         <!-- Signup box -->
         <!-- Reg code [user-specific] NOT required or detected in query params AND Invite reg code [not user-specific] NOT required or detected in query params -->
         <AuthLayout
            v-else-if="
               (!clientSettings.registrationCodeRequired || presetRegistrationCode) &&
               (!clientSettings.portalInvitationOnlyRegistration || presetInvitationRegCode)
            "
            :heading="$t('authentication.create_account')"
            data-test="signup-form"
         >
            <template v-slot:description>
               <span>{{ $t("authentication.contribute") }}</span>
            </template>

            <template v-slot:form>
               <SignUpBox
                  :showLoginWithGoogle="showLoginWithGoogle"
                  :signupPolicy="signupPolicyContent"
                  :communicationsPolicy="communicationsPolicyContent"
                  :oauthError="state.oauthError"
                  :redirect="state.redirect"
                  @error="onRegError"
                  @is-completed="onRegComplete"
               />
            </template>

            <template v-slot:bottom>
               <div>
                  <span>{{ $t("authentication.account_exist") }} </span>
                  <router-link
                     :to="{
                        name: 'login',
                        params: { locale: $route.params?.locale },
                        query: filteredQueryParams,
                     }"
                     data-cy="to-login"
                     class="text-bolder mx-1 border-b text-brand-dark"
                  >
                     {{ $t("authentication.login") }}
                  </router-link>
               </div>
            </template>
         </AuthLayout>

         <!-- Reg code [user-specific] or Invitation reg code [not user specific] required (show only if content is set) -->
         <AuthLayout
            v-else-if="registrationCodeContent"
            :heading="registrationCodeContent.heading"
            data-test="reg-or-invite-code-rqd"
         >
            <template v-slot:description>
               <div class="flex-col items-center" data-cy="missing-code-or-id">
                  <RichTextRenderer :document="registrationCodeContent.content" />

                  <LinkButton
                     v-if="organisationInfo.email"
                     :url="`mailto:${organisationInfo.email}`"
                     isExternal
                     class="mt-8 w-full"
                  >
                     {{ $t("authentication.get_in_touch") }}
                  </LinkButton>
               </div>
            </template>

            <template v-slot:bottom>
               <router-link
                  :to="{ name: 'home', params: { locale: $route.params?.locale } }"
                  class="text-regular text-brand-dark underline"
               >
                  {{ $t("redirects.back_to_home") }}
               </router-link>
            </template>
         </AuthLayout>
      </transition>
   </PageLoader>
</template>

<script setup lang="ts">
import { Head } from "@unhead/vue/components";
import SignUpBox from "./SignUpBox.vue";
import { useAppStore } from "@/stores/appStore";
import { useClientSettingsKey } from "@/plugins/useClientSettings";
import { useBaseContentStore } from "@/stores/baseContentStore";
import ContentApi, { type SignupContent } from "@/data/api-layer/contentApi";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { useApiRequest } from "@/composables/useApiRequest";
import { useRoute } from "vue-router";

const appStore = useAppStore();
const baseContentStore = useBaseContentStore();
const clientSettings = inject(useClientSettingsKey);
const { t: $t } = useI18n();
const { isLoading, execRequest, loadErrors } = useApiRequest();
const showLoginWithGoogle = ref<boolean>(true);
const $route = useRoute();

// data & requests
// -----------------------------------------
interface State {
   regCodeError: string | null;
   oauthError: string | null;
   isRegCompleted: boolean;
   userEmail: string | null;
   signupContent: SignupContent;
   redirect: string | null;
}

const state = reactive<State>({
   // the [user-specific or not] code entered for registration didnt match the code in the database
   // or another parameter passed to the reg function (e.g. site) was invalid
   regCodeError: null,
   oauthError: null,
   isRegCompleted: false,
   userEmail: null,
   signupContent: null, // fetched from content BE
   redirect: null,
});

onMounted(async () => {
   checkForOAuthErrors();
   // Note redirect information to pass to SignUpBox
   if ($route.query?.redirect) state.redirect = $route.query.redirect as string;

   showLoginWithGoogle.value = clientSettings.googleSignInEnabled;

   const response = await execRequest(() => ContentApi.getSignup());
   if (!response) return;

   state.signupContent = response.content;
});

// computed
// -----------------------------------------
const filteredQueryParams = computed(() => {
   const query = { ...$route.query };
   delete query.error_code;
   return query;
});

const organisationInfo = computed(() => {
   if (!baseContentStore.content) return null;

   return baseContentStore.content.organisationInfo;
});

const registrationCodeContent = computed(() => {
   // there is no reg code feature or the page is still loading
   if (
      (!clientSettings.registrationCodeRequired && !clientSettings.portalInvitationOnlyRegistration) ||
      isLoading.value
   ) {
      return null;
   }

   const data = state.signupContent?.registrationCode;
   return data;
});

const signupPolicyContent = computed(() => {
   if (isLoading.value) return null;

   const data = state.signupContent?.signupPolicy?.content;
   return data;
});

const communicationsPolicyContent = computed(() => {
   if (isLoading.value) return null;

   const data = state.signupContent?.communicationsPolicy?.content;
   return data;
});

const presetRegistrationCode = computed(() => {
   return appStore.readVisitSignupParams.registrationCode;
});

const presetInvitationRegCode = computed(() => {
   return appStore.readVisitSignupParams.invitationCode;
});

// methods
// -----------------------------------------

const checkForOAuthErrors = () => {
   // If a user is logging in with email+password, and they submit an invalid registration code,
   // invite code or site code, the error will be emitted from the SignUpBox component (onRegError)
   // If a user is logging in with google, they will be redirected back to this page with the error as a query param
   // The same will happen if anything else goes wrong during the identification process
   let oauthErr = $route.query?.error_code;
   // Handle errors one by one
   if (Array.isArray(oauthErr)) oauthErr = oauthErr[0];

   if (oauthErr) {
      // Errors around reg/invite codes and site ids are handled on this page
      if (oauthErr === "invalid_registration_code") state.regCodeError = "Invalid registration code";
      else if (oauthErr === "invalid_invitation_code") state.regCodeError = "Invalid invitation code";
      else if (oauthErr === "invalid_site_identifier") state.regCodeError = "Invalid StudySite identifier";
      else {
         // Errors around authenticating with Google are passed down to the SignUpBox
         state.oauthError = oauthErr;
      }
   }
};

const onReset = () => {
   state.regCodeError = "";
};

const onRegError = (error) => {
   state.regCodeError = error;
};

const onRegComplete = (userEmail) => {
   state.isRegCompleted = true;
   state.userEmail = userEmail;
};
</script>

<style scoped lang="scss">
.opacity-slow {
   transition: opacity 0.9s ease-in-out;
}

.reg-with-code-error-message {
   margin-block-start: var(--size-base-24);
}
</style>
